import { getTransactions } from './../megaMenuUtils';
import { Transaction } from './transactions';

export type ContentItems =
  | 'REGISTER'
  | 'MANAGE'
  | 'ANNUAL_FILING'
  | 'DEREGISTER'
  | 'OTHERS'
  | 'BUY_INFORMATION'
  | 'SUBSCRIBE_APIS';

export type L1Persona = {
  id: number;
  label: string;
  description: string;
  transactions: Transaction[];
};

export type L2Persona = {
  id: number;
  label: string;
  description: string;
  headers: {
    id: string;
    label: string;
    transactions: Transaction[];
  }[];
};

export type TabsContent = {
  personas: (L1Persona | L2Persona)[];
};

export type PlainContent = {
  transactions: Transaction[];
};

export type TMegaMenuContent = (TabsContent | PlainContent) & {
  heading: string;
  description: string;
  overviewHref?: string;
};

export const MEGA_MENU_CONTENT: Record<ContentItems, TMegaMenuContent> = {
  REGISTER: {
    heading: 'Register',
    description:
      'Access eServices to register a new entity or register as a professional',
    personas: [
      {
        id: 0,
        label: 'Register new entity',
        description:
          'eServices to register different business entity types and redomiciliation of foreign business entity',
        transactions: getTransactions(
          [
            'applyforNewBusiness',
            'registerNewBusiness',
            'applyOmit',
            'transferRegistration',
          ],
          'REGISTER',
        ),
      },
      {
        id: 1,
        label: 'Register as professional',
        description:
          'eServices to register as corporate service provider, registered qualified individual, and public accountant',
        transactions: getTransactions(
          ['registerCorporate', 'registerIndividual', 'registerAccountant'],
          'REGISTER',
        ),
      },
      {
        id: 2,
        label: 'Update professional status',
        description:
          'eService to register the status of public accounting entity',
        transactions: getTransactions(['registerPublic'], 'REGISTER'),
      },
      {
        id: 3,
        label: 'Group of companies',
        description:
          'eService for holding companies to register authorised persons for filing access in Bizfile to conduct transactions with ACRA on behalf of the Group of Companies',
        transactions: getTransactions(['registerFiling'], 'REGISTER'),
      },
    ],
  },
  MANAGE: {
    heading: 'Manage',
    description:
      ' Access eServices to manage and update entity details and professional information',
    personas: [
      {
        id: 0,
        label: 'Local company',
        description: 'eServices to manage and update details of local company',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              [
                'updateEntity',
                'updatePersonnel',
                'appointWithDrawPosition',
                'updateShareholder',
                'updateChangeFinancial',
                'updateListMembers',
                'updateRegisterRegistrable',
                'fileNoticeResolution',
                'updateRegistersNominee',
                'fileNoticeError',
                'fileOrderCourt',
                'fileAtleration',
                'notifyCessation',
              ],
              'MANAGE',
            ),
          },
          {
            id: 'updateShares',
            label: 'Update shares',
            transactions: getTransactions(
              ['updateShareInformation', 'reduceShareCapital'],
              'MANAGE',
            ),
          },
          {
            id: 'registerUpdateCharges',
            label: 'Register or update charges',
            transactions: getTransactions(
              ['registerNewCharge', 'fileVariation'],
              'MANAGE',
            ),
          },
          {
            id: 'extensionOfTime',
            label: 'Extension of time',
            transactions: getTransactions(
              ['applyExtensionTime'],
              'ANNUAL_FILING',
            ),
          },
          {
            id: 'restructureOrConvertEntityType',
            label: 'Restructure or convert entity type',
            transactions: getTransactions(
              ['convertToLLP', 'convertCompanyTypes', 'registerAmalgamation'],
              'MANAGE',
            ),
          },
          {
            id: 'groupOfCompanies',
            label: 'Group of companies',
            transactions: getTransactions(
              [
                'maintenanceSubsidiaries',
                'maintenanceGroupSecretary',
                'maintenanceFilingAccess',
              ],
              'MANAGE',
            ),
          },
        ],
      },
      {
        id: 1,
        label: 'Foreign company',
        description:
          'eServices to manage and update details of foreign company',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              [
                'updateEntity',
                'updatePersonnel',
                'appointWithDrawPosition',
                'updateChangeFinancial',
                'updateRegisterRegistrable',
                'fileNoticeError',
                'updateRegistersNominee',
                'fileOrderCourt',
              ],
              'MANAGE',
            ),
          },
          {
            id: 'registerUpdateCharges',
            label: 'Register or update charges',
            transactions: getTransactions(
              ['registerNewCharge', 'fileVariation'],
              'MANAGE',
            ),
          },
          {
            id: 'extensionOfTime',
            label: 'Extension of time',
            transactions: getTransactions(
              ['applyExtensionTime'],
              'ANNUAL_FILING',
            ),
          },
        ],
      },
      {
        id: 2,
        label: 'Sole proprietorship/partnership',
        description:
          'eServices to manage and update details of sole proprietorship/partnership',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              [
                'updateEntity',
                'updatePersonnel',
                'appointWithDrawPosition',
                'fileNoticeDeath',
                'fileNoticeError',
                'fileOrderCourt',
              ],
              'MANAGE',
            ),
          },
          {
            id: 'restructureOrConvertEntityType',
            label: 'Restructure or convert entity type',
            transactions: getTransactions(['convertToLLP'], 'MANAGE'),
          },
          {
            id: 'renewal',
            label: 'Renewal',
            transactions: getTransactions(['renewBusiness'], 'MANAGE'),
          },
        ],
      },
      {
        id: 3,
        label: 'Limited liability partnership',
        description:
          'eServices to manage and update details of limited liability partnership',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              [
                'updateEntity',
                'updatePersonnel',
                'appointWithDrawPosition',
                'updateRegisterRegistrable',
                'fileNoticeError',
                'fileOrderCourt',
                'notifyCessation',
              ],
              'MANAGE',
            ),
          },
        ],
      },
      {
        id: 4,
        label: 'Limited partnership',
        description:
          'eServices to manage and update details of limited partnership',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              [
                'updateEntity',
                'updatePersonnel',
                'appointWithDrawPosition',
                'fileNoticeError',
                'fileOrderCourt',
                'notifyCessation',
              ],
              'MANAGE',
            ),
          },
          {
            id: 'renewal',
            label: 'Renewal',
            transactions: getTransactions(
              ['renewLimitedPartnership'],
              'MANAGE',
            ),
          },
        ],
      },
      {
        id: 5,
        label: 'Public accounting firm',
        description:
          'eServices to manage and update details of public accounting firm',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              ['updateEntity', 'updatePersonnel', 'appointWithDrawPosition'],
              'MANAGE',
            ),
          },
          {
            id: 'restructureOrConvertEntityType',
            label: 'Restructure or convert entity type',
            transactions: getTransactions(['convertToLLP'], 'MANAGE'),
          },
        ],
      },
      {
        id: 6,
        label: 'Corporate service provider',
        description:
          'eServices to manage and update details of corporate service provider',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              [
                'updateCSPInformation',
                'updateRegisteredQualified',
                'maintenanceCSP',
                'maintenanceFilingAccessCSP',
              ],
              'MANAGE',
            ),
          },
          {
            id: 'renewal',
            label: 'Renewal',
            transactions: getTransactions(
              ['renewRegistrationCSP', 'renewRegistrationRegisteredQualified'],
              'MANAGE',
            ),
          },
        ],
      },
      {
        id: 7,
        label: 'Public accountant',
        description:
          'eServices to manage and update details of public accountant',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(['updatePublicAccountant'], 'MANAGE'),
          },
        ],
      },
      {
        id: 8,
        label: 'Approved liquidator',
        description:
          'eServices to manage and update details of approved liquidator',
        headers: [
          {
            id: 'updateInformation',
            label: 'Update information',
            transactions: getTransactions(
              ['updateApprovedLiquidators'],
              'MANAGE',
            ),
          },
        ],
      },
    ],
  },
  ANNUAL_FILING: {
    heading: 'Annual filing',
    description:
      'Access eServices to file annual returns, annual declarations and financial statements',
    personas: [
      {
        id: 0,
        label: 'Local company',
        description:
          'eServices for annual filing and exemption for local company',
        headers: [
          {
            id: 'fileAnnualReturns',
            label: 'File annual returns',
            transactions: getTransactions(
              ['fileAnnualReturns', 'applyExemption'],
              'ANNUAL_FILING',
            ),
          },
          {
            id: 'extensionOfTime',
            label: 'Extension of time',
            transactions: getTransactions(
              ['applyExtensionTimeAGM'],
              'ANNUAL_FILING',
            ),
          },
          {
            id: 'notificationOfAGM',
            label: 'Notification of annual general meeting',
            transactions: getTransactions(
              ['fileNotificationAGM'],
              'ANNUAL_FILING',
            ),
          },
        ],
      },
      {
        id: 1,
        label: 'Foreign company',
        description:
          'eServices for annual filing and exemption for foreign company',
        headers: [
          {
            id: 'fileFinancialStatements',
            label: 'File financial statements',
            transactions: getTransactions(
              ['fileFinancialStatements', 'applyExemption'],
              'ANNUAL_FILING',
            ),
          },
          {
            id: 'extensionOfTime',
            label: 'Extension of time',
            transactions: getTransactions(
              ['applyExtensionTimeFS'],
              'ANNUAL_FILING',
            ),
          },
        ],
      },
      {
        id: 2,
        label: 'Limited liability partnership',
        description:
          'eServices for annual filing and exemption for limited liability partnership',
        headers: [
          {
            id: 'fileAnnualDeclarations',
            label: 'File annual declarations',
            transactions: getTransactions(
              ['fileAnnualDeclarations'],
              'ANNUAL_FILING',
            ),
          },
          {
            id: 'extensionOfTime',
            label: 'Extension of time',
            transactions: getTransactions(
              ['applyExtensionTimeDeclarations'],
              'ANNUAL_FILING',
            ),
          },
        ],
      },
    ],
  },
  DEREGISTER: {
    heading: 'Deregister',
    description:
      'Access eServices to close an entity or cancel professional registration',
    personas: [
      {
        id: 0,
        label: 'Local company',
        description: 'eServices to close a local company',
        headers: [
          {
            id: 'cancelProfessionalStatus',
            label: 'Cancel professional status',
            transactions: getTransactions(
              ['applyToRevokePublicAccounting'],
              'DEREGISTER',
            ),
          },
          {
            id: 'strikingOff',
            label: 'Striking off',
            transactions: getTransactions(
              [
                'applyStrikingOff',
                'manageStrikingOff',
                'submitClearObjections',
              ],
              'DEREGISTER',
            ),
          },
          {
            id: 'windingUp',
            label: 'Winding up',
            transactions: getTransactions(
              [
                'applyWindingUp',
                'manageWindingUp',
                'fileObjectionEarlyDissolution',
              ],
              'DEREGISTER',
            ),
          },
          {
            id: 'simplifiedWindingUp',
            label: 'Simplified winding up',
            transactions: getTransactions(
              ['notifySimplifiedRestructuring', 'fileSimplifiedWindinUp'],
              'DEREGISTER',
            ),
          },
          {
            id: 'receivership',
            label: 'Receivership',
            transactions: getTransactions(
              [
                'fileAppointmentCessation',
                'fileReceiversAccount',
                'fileReceiverStatementAffairs',
              ],
              'DEREGISTER',
            ),
          },
          {
            id: 'judicialManagement',
            label: 'Judicial management',
            transactions: getTransactions(
              ['applyJudicialManagement', 'manageJudicialManagement'],
              'DEREGISTER',
            ),
          },
          {
            id: 'groupOfCompanies',
            label: 'Group of companies',
            transactions: getTransactions(
              ['deregisterFilingAccess'],
              'DEREGISTER',
            ),
          },
        ],
      },
      {
        id: 1,
        label: 'Foreign company',
        description: 'eServices to close a foreign company',
        headers: [
          {
            id: 'cessationOrDissolution',
            label: 'Cessation or dissolution',
            transactions: getTransactions(
              ['ceaseDissolveBusinessEntity'],
              'DEREGISTER',
            ),
          },
          {
            id: 'strikingOff',
            label: 'Striking off',
            transactions: getTransactions(
              [
                'applyStrikingOff',
                'manageStrikingOff',
                'submitClearObjections',
              ],
              'DEREGISTER',
            ),
          },
        ],
      },
      {
        id: 2,
        label: 'Sole proprietorship/partnership',
        description: 'eServices to cease a sole proprietorship/patnership ',
        headers: [
          {
            id: 'cessationOrDissolution',
            label: 'Cessation or dissolution',
            transactions: getTransactions(['fileCessation'], 'DEREGISTER'),
          },
        ],
      },
      {
        id: 3,
        label: 'Limited liability partnership',
        description: 'eServices to cease a limited liability partnership',
        headers: [
          {
            id: 'cancelProfessionalStatus',
            label: 'Cancel professional status',
            transactions: getTransactions(
              ['applyToRevokePublicAccounting'],
              'DEREGISTER',
            ),
          },
          {
            id: 'strikingOff',
            label: 'Striking off',
            transactions: getTransactions(
              [
                'applyStrikingOff',
                'manageStrikingOff',
                'submitClearObjections',
              ],
              'DEREGISTER',
            ),
          },
          {
            id: 'windingUp',
            label: 'Winding up',
            transactions: getTransactions(
              ['applyWindingUp', 'manageWindingUp'],
              'DEREGISTER',
            ),
          },
          {
            id: 'receivership',
            label: 'Receivership',
            transactions: getTransactions(
              [
                'fileAppointmentCessation',
                'fileReceiversAccount',
                'fileReceiverStatementAffairs',
              ],
              'DEREGISTER',
            ),
          },
        ],
      },
      {
        id: 4,
        label: 'Limited partnership',
        description: 'eServices to cease a limited partnership',
        headers: [
          {
            id: 'cessationOrDissolution',
            label: 'Cessation or dissolution',
            transactions: getTransactions(
              ['ceaseDissolveBusinessEntity'],
              'DEREGISTER',
            ),
          },
        ],
      },
      {
        id: 5,
        label: 'Public accounting firm',
        description: 'eServices to cease a public accounting firm',
        headers: [
          {
            id: 'cancelProfessionalStatus',
            label: 'Cancel professional status',
            transactions: getTransactions(
              ['applyToRevokePublicAccounting'],
              'DEREGISTER',
            ),
          },
          {
            id: 'cessationOrDissolution',
            label: 'Cessation or dissolution',
            transactions: getTransactions(
              ['ceaseDissolveBusinessEntity'],
              'DEREGISTER',
            ),
          },
        ],
      },
      {
        id: 6,
        label: 'Corporate service provider',
        description:
          'eServices to cease registration as corporate service provider',
        headers: [
          {
            id: 'deregisterProfessional',
            label: 'Deregister as professional',
            transactions: getTransactions(
              [
                'deregisterRegistrationCSP',
                'deregisterRegistrationRegisteredQualified',
              ],
              'DEREGISTER',
            ),
          },
        ],
      },
      {
        id: 7,
        label: 'Public accountant',
        description: 'eServices to cease public accountant registration',
        headers: [
          {
            id: 'deregisterProfessional',
            label: 'Deregister as professional',
            transactions: getTransactions(
              ['deregisterRegistrationPublicAccountant'],
              'DEREGISTER',
            ),
          },
        ],
      },
      {
        id: 8,
        label: 'Approved liquidator',
        description: 'eServices to cease registration as approved liquidator',
        headers: [
          {
            id: 'deregisterProfessional',
            label: 'Deregister as professional',
            transactions: getTransactions(
              ['withdrawApprovedLiquidator'],
              'DEREGISTER',
            ),
          },
        ],
      },
    ],
  },
  OTHERS: {
    heading: 'Others',
    description: 'Additional eServices to support your business needs ',
    transactions: getTransactions(
      [
        'valueAddedServices',
        'refund',
        'generalLodgement',
        'applyRestoreBusinessEntity',
        'applyConsentResign',
        'fileNameComplaint',
      ],
      'OTHERS',
    ),
  },
  BUY_INFORMATION: {
    heading: 'Buy information',
    description:
      'Explore a wide range of business information products tailored to meet your business needs',
    overviewHref: '/overview/buy-information',
    personas: [
      {
        id: 0,
        label: 'Information products',
        description:
          'Browse information products offering business insights and data to support informed decision making and drive business growth',
        transactions: getTransactions(
          [
            'businessProfile',
            'corporateCompliance',
            'peopleProfile',
            'extracts',
            'certificates',
            'registers',
          ],
          'BUY_INFORMATION',
        ),
      },
      {
        id: 1,
        label: 'Download',
        description:
          'Access and download your purchased information products with ease',
        transactions: getTransactions(['productDownload'], 'BUY_INFORMATION'),
      },
      {
        id: 2,
        label: 'Authenticate',
        description:
          'Verify the authenticity of your information products for added confidence',
        transactions: getTransactions(
          ['trustBarAuthentication', 'pdfProductAuthentication'],
          'BUY_INFORMATION',
        ),
      },
      {
        id: 3,
        label: 'Others',
        description:
          'Access other useful information resources to meet your business needs',
        transactions: getTransactions(
          ['variableCapitalCompanies', 'requestForPermissionDisc'],
          'BUY_INFORMATION',
        ),
      },
    ],
  },
  SUBSCRIBE_APIS: {
    heading: 'Subscribe APIs',
    description:
      'Explore and empower your digital solutions with real-time business entity data',
    overviewHref: '/apimarketplace',
    transactions: getTransactions(
      ['gettingStarted', 'dataApiCategories', 'apimManagement'],
      'SUBSCRIBE_APIS',
    ),
  },
};

export const MEGA_MENU_URLS = {
  REGISTER: {
    APPLY_NEW_BUSINESS: 'name-application/instruction',
    REGISTER_NEW_BUSINESS: 'entity-registration/instruction',
  },
  SUBSCRIBE: {
    GETSTARTED: 'getting-started',
    DATA_API_CATEGORY: 'categories/data-api',
    TRANSACTIONAL_API_CATEGORY: 'categories/transactional-api',
    MARKETPLACE_MANAGEMENT: 'management',
  },
};
